import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ConversationSummaryShape, type StoredMessageShape } from '@/lib/api';

const CONVERSATIONS_KEY = ['conversations', 'list'] as const;
const messagesKey = (conversationId: string) =>
  ['conversations', conversationId, 'messages'] as const;

/**
 * "Your conversations" — the inbox list. Auto-revalidates every 30s as a
 * fallback for when the WebSocket happens to be down. Live updates come from
 * the WS subscription, which mutates the cache directly.
 */
export function useConversationsQuery(token: string) {
  return useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => api.conversations.list(token),
    staleTime: 30_000,
    enabled: token.length > 0,
  });
}

export function useMessagesQuery(token: string, conversationId: string | null) {
  return useQuery({
    queryKey: conversationId ? messagesKey(conversationId) : ['conversations', '_empty'],
    queryFn: () =>
      conversationId
        ? api.conversations.listMessages(token, conversationId)
        : Promise.resolve({ messages: [] }),
    staleTime: 30_000,
    enabled: token.length > 0 && conversationId !== null,
  });
}

export function useSendMessageMutation(token: string, conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; replyToMessageId?: string | null }) =>
      api.conversations.sendMessage(token, conversationId, body),
    onSuccess: ({ message }) => {
      // Optimistically prepend; the WS broadcast will arrive a few ms later
      // with the same id, so the merge below de-dupes.
      queryClient.setQueryData<{ messages: StoredMessageShape[] }>(
        messagesKey(conversationId),
        (prev) => mergeMessage(prev?.messages ?? [], message),
      );
    },
  });
}

export function useCreateConversationMutation(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.conversations.create.bind(null, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    },
  });
}

export function useMarkConversationReadMutation(token: string, conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.conversations.markRead(token, conversationId),
    onSuccess: () => {
      // Optimistically clear the unread flag for this thread.
      queryClient.setQueryData<{ conversations: ConversationSummaryShape[] }>(
        CONVERSATIONS_KEY,
        (prev) =>
          prev
            ? {
                conversations: prev.conversations.map((c) =>
                  c.id === conversationId ? { ...c, unread: false } : c,
                ),
              }
            : prev,
      );
    },
  });
}

/* ─── Cache helpers used by the WS subscriber ──────────────────────────── */

export const conversationsCache = {
  /** De-dup-aware insert. */
  appendMessage(
    queryClient: import('@tanstack/react-query').QueryClient,
    conversationId: string,
    message: StoredMessageShape,
  ) {
    queryClient.setQueryData<{ messages: StoredMessageShape[] }>(
      messagesKey(conversationId),
      (prev) => mergeMessage(prev?.messages ?? [], message),
    );
    queryClient.setQueryData<{ conversations: ConversationSummaryShape[] }>(
      CONVERSATIONS_KEY,
      (prev) => bumpConversation(prev?.conversations ?? [], conversationId, message.createdAt),
    );
  },
};

function mergeMessage(
  existing: StoredMessageShape[],
  incoming: StoredMessageShape,
): { messages: StoredMessageShape[] } {
  if (existing.some((m) => m.id === incoming.id)) {
    return { messages: existing };
  }
  return { messages: [...existing, incoming] };
}

function bumpConversation(
  list: ConversationSummaryShape[],
  conversationId: string,
  lastMessageAt: number,
): { conversations: ConversationSummaryShape[] } {
  let touched = false;
  const updated = list.map((c) => {
    if (c.id !== conversationId) return c;
    touched = true;
    return { ...c, lastMessageAt, unread: true };
  });
  if (!touched) return { conversations: list };
  // Move the bumped conversation to the top.
  updated.sort((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0));
  return { conversations: updated };
}
